import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface PendingSlotProps {
  /**
   * While true, the control is not offered at all: it is laid out exactly as
   * it will be (so nothing moves when the answer arrives) but invisible and
   * inert, under a pulsing placeholder. When false, the control is rendered
   * as-is.
   */
  readonly pending: boolean;
  readonly children: ReactNode;
  readonly className?: string;
}

/**
 * A placeholder for a control whose availability is not known yet — e.g. the
 * person's role while `/me` loads.
 *
 * Invariant: a pending control can never be activated or focused (`inert`,
 * a disabled `fieldset`, `visibility: hidden`) and is not announced (`aria-hidden`); the slot keeps
 * the control's exact box, so there is no layout shift when it resolves to an
 * enabled control or to a disabled one with its reason. The screen announces
 * "checking" once, elsewhere; the slot itself is silent.
 */
export function PendingSlot({ pending, children, className }: PendingSlotProps) {
  if (!pending) return <>{children}</>;
  return (
    <span
      className={cn('relative inline-flex max-w-full', className)}
      data-testid="control-pending"
    >
      <span className="invisible inline-flex max-w-full" aria-hidden="true" inert>
        {/* `disabled` as well as `inert`: a form control inside cannot be
            focused or submitted even where `inert` is not implemented. */}
        <fieldset disabled className="contents">
          {children}
        </fieldset>
      </span>
      <span
        aria-hidden="true"
        className="absolute inset-0 animate-pulse rounded-md bg-muted motion-reduce:animate-none"
      />
    </span>
  );
}
